import { Search } from 'lucide-react'
import type React from 'react'
import DefinitionPopover from '@/components/glossary/DefinitionPopover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GLOSSARY_TERMS } from '@/constants'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { SearchByBendingMomentProps } from '@/types/mainPageComponents'
import { PanelSuggestions } from './PanelSuggestions'

export const SearchByBendingMoment: React.FC<SearchByBendingMomentProps> = ({
  bendingMoment,
  bendingMomentUnit,
  selectedSteelGrade,
  showBendingSuggestions,
  bendingSuggestions,
  bendingSearchLoading,
  steelGradeOptions,
  steelGradesLoading,
  bendingSearchRef,
  onBendingMomentChange,
  onUnitChange,
  onSteelGradeChange,
  onShowSuggestionsChange,
  onSelectPanel,
  onSearch,
  onFocus,
}) => {
  const { t } = useLanguage()

  const unitDisplay = bendingMomentUnit === 'metric' ? 'kNm/m' : 'kip-in/ft'

  return (
    <div className="mb-12">
      <h2
        className="text-3xl font-bold text-gray-900 mb-2"
        style={{
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        {t('main_page.search.by_bending_moment.search_by')}{' '}
        <DefinitionPopover
          term={GLOSSARY_TERMS.BENDING_MOMENT_CAPACITY.handle}
          className="text-primary hover:text-primary"
          side="top"
          align="center"
        >
          {t(`glossary.${GLOSSARY_TERMS.BENDING_MOMENT_CAPACITY.handle}`)}
        </DefinitionPopover>
      </h2>
      <p className="text-gray-600 mb-6">{t('main_page.search.by_bending_moment.description')}</p>
      <div className="flex flex-col md:flex-row gap-4 md:items-center">
        <div className="order-1 md:order-2">
          <Select value={bendingMomentUnit} onValueChange={onUnitChange}>
            <SelectTrigger className="w-full md:w-48 py-6 text-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="metric">
                {t('main_page.search.by_bending_moment.units.metric')}
              </SelectItem>
              <SelectItem value="imperial">
                {t('main_page.search.by_bending_moment.units.imperial')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="order-2 md:order-3">
          <Select
            value={selectedSteelGrade?.toString() || '3'}
            onValueChange={(value) => {
              onSteelGradeChange(value ? parseInt(value, 10) : 3)
            }}
          >
            <SelectTrigger className="w-full md:w-48 py-6 text-lg">
              <SelectValue placeholder={t('main_page.search.by_bending_moment.steel_grade')} />
            </SelectTrigger>
            <SelectContent>
              {steelGradesLoading ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {t('main_page.search.by_bending_moment.loading_steel_grades')}
                </div>
              ) : steelGradeOptions ? (
                steelGradeOptions.map((grade: { value: number; label: string }) => (
                  <SelectItem key={grade.value} value={grade.value.toString()}>
                    {grade.label}
                  </SelectItem>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">No steel grades found</div>
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 relative order-3 md:order-1" ref={bendingSearchRef}>
          <Input
            type="number"
            value={bendingMoment}
            onChange={(e) => {
              onBendingMomentChange(e.target.value)
              onShowSuggestionsChange(e.target.value.length > 0)
            }}
            onFocus={onFocus}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearch()
              }
            }}
            placeholder={bendingMomentUnit === 'metric' ? 'e.g., 150' : 'e.g., 110'}
            className="text-lg py-6 px-4"
          />

          {showBendingSuggestions && (
            <PanelSuggestions
              isLoading={bendingSearchLoading}
              suggestions={bendingSuggestions}
              searchValue={bendingMoment}
              unitDisplay={unitDisplay}
              onSelectPanel={onSelectPanel}
              exactMatchHeaderKey="main_page.search.by_bending_moment.exact_match_header"
              exactMatchKey="main_page.search.by_section_modulus.exact_match"
              valueType="bendingMoment"
            />
          )}
        </div>
        <Button
          onClick={onSearch}
          disabled={!bendingMoment.trim()}
          size="lg"
          className="p-6 md:px-8 order-4"
        >
          <Search className="w-5 h-5 md:mr-2" />
          <span className="hidden md:inline">{t('main_page.search.by_name.button')}</span>
        </Button>
      </div>
    </div>
  )
}
